<script setup>

import { computed, ref, watch } from 'vue'
import DatePicker from '@/components/Forms/DatePicker.vue'
import { DatePickerObj } from '@/models/utils/InputUtils.js'
import { ButtonSize, ButtonType } from '@/components/Forms/Buttons/SubmitButtonConstants.js'
import SubmitButton from '@/components/Forms/Buttons/SubmitButton.vue'
import { Toaster } from '@/models/utils/Toaster.js'
import { ModalButtonData } from '@/components/Alerts/ModalButtonData.js'
import MultiButtonModal from '@/components/Alerts/MultiButtonModal.vue'
import BaseModal from '@/components/Alerts/BaseModal.vue'
import InputText from '@/components/Forms/InputText.vue'
import UserLookupComboBox from '@/components/Forms/UserLookupComboBox.vue'
import { useAuthStore } from '@/stores/authStore.js'
import CheckBox from '@/components/Forms/CheckBox.vue'
import { scheduledReportsApi } from '@/api/ScheduledReports/ScheduledReportsApi.js'
import { Util } from '@/models/utils/Util.js'
import { Format } from '@/models/utils/FormatUtils.js'
import Validation from '@/models/utils/Validation.js'
import { endOfDay, endOfMonth, endOfQuarter, endOfWeek, endOfYear, startOfDay, startOfMonth, startOfQuarter, startOfWeek, startOfYear } from 'date-fns'

const props = defineProps({
	open: { type: Boolean, default: false },
	report: { type: Object, default: null },
	modalError: { type: Object, default: () => ({ 'error_eh': false, 'error_msg': 'There was an error loading tags. Please try reloading the page.' }) },
});

/**
 * Validation object to add/remove validation errors
 * @type {[Validation] extends [Ref] ? IfAny<Validation, Ref<Validation>, Validation> : Ref<UnwrapRef<Validation>, UnwrapRef<Validation> | Validation>}
 */
const validation = ref(new Validation());
const isSaving = ref(false)
const authStore = useAuthStore()
const emit = defineEmits(['evCloseModal']);
const showDiscardChangesModal = ref(false);
const toaster = new Toaster();

const date_from = ref(null)
const date_to = ref(null)
const error = ref('');
const selectedRecipients = ref([]);
const report_name = ref('')
const include_default_recipients = ref(true)

const init_data = ref({})

// Populate the form from the report's existing cron expression each time it opens.
watch(() => [props.open, props.report],
	() =>
	{
		if (!props.open || !props.report) return;

		report_name.value = props.report.name;
		include_default_recipients.value = props.report.include_default_recipients === 1
		selectedRecipients.value = props.report.recipients?.filter(function (recipient) { return !recipient.is_default_recipient }) ?? []
		error.value = '';

		date_from.value = new DatePickerObj(getInitialFromDate(), {
			dateFormat: 'Y-m-d',
			altFormat: 'm-d-Y',
			minuteIncrement: 1,
		})

		date_to.value = new DatePickerObj(getInitialToDate(), {
			dateFormat: 'Y-m-d',
			altFormat: 'm-d-Y',
			minuteIncrement: 1,
		})

		init_data.value = Util.deepCopy(
			{
				"date_from": date_from.value.date,
				"date_to": date_to.value.date,
				"include_default_recipients": include_default_recipients.value,
				"selectedRecipients": selectedRecipients.value
			}
		)
	},
	{ immediate: true }
);

watch (() => [include_default_recipients.value, selectedRecipients.value], () => {
	validateFields()
})

/**
 * Check if all fields are valid
 * @returns {boolean}
 */
function validateFields()
{
	let valid = true;
	validation.value.removeAll()
	if (!include_default_recipients.value && selectedRecipients.value.length === 0)
	{
		validation.value.add('recipients', 'At least one recipient is required.')
		valid = false;
	}

	if (props.report.settings.require_date_range)
	{
		valid &= validateDate([date_from.value.getDate()], 'datepicker_from')
		valid &= validateDate([date_to.value.getDate()], 'datepicker_to')
	}

	return valid
}

/**
 * Submit the report to run next time scheduler checks for pending reports.
 */
function submit()
{
	if (!validateFields()) return;

	try
	{
		error.value = '';
		isSaving.value = true
		let settings = {}

		if (props.report.settings.require_date_range) {
			settings = {
				"date_from": date_from.value.getDate(),
				"date_to": date_to.value.getDate(),
				"timezone": Intl.DateTimeFormat().resolvedOptions().timeZone //Used to parse the date properly and allows us to see the same date/time in the logs
			}
		}

		settings['include_default_recipients'] = include_default_recipients.value

		const recipientIds = selectedRecipients.value.map(r => { return r.id })

		scheduledReportsApi.runReport(props.report.id, settings, recipientIds, onSubmitComplete)
	}
	catch (e)
	{
		toaster.error(e.message)
	}

}

/**
 * Called after submit completes
 * @param p_response Response from submission
 * @param p_error_eh True if there was an error
 */
function onSubmitComplete(p_response, p_error_eh)
{
	try
	{
		isSaving.value = false
		if (p_error_eh)
		{
			p_response.showErrorBanner()
		}
		else
		{
			const data = p_response.getData();
			if (data && data.success)
			{
				emit('evCloseModal', 'run', true, { id: props.report.id });
			}
			else if (data && !data.success)
			{
				toaster.error(data.error_message)
			}
			else
			{
				toaster.error("Failed to create report.")
			}
		}
	}
	catch (e)
	{
		if (authStore.isDeveloper) console.error(e)
	}
}

const changesMade = computed(() => {
	return (
		(init_data.value.date_from !== date_from.value.date) ||
		(init_data.value.date_to !== date_to.value.date) ||
		(include_default_recipients.value !== init_data.value.include_default_recipients) ||
		(selectedRecipients.value.length > 0)
	)
})

/**
 * Checks if there are any changes made to the modal
 * @returns {boolean}

function changesMade()
{
	return false;//(document_name.value !== '' || selectedTags.value.length > 0 || filesQueued.value.length > 0 || (toggleBulkAdd.value && !isEmpty(tags.value)))
} */

/**
 * Check if there's any changes, if not, proceed with closing the modal
 * @param p_reload_eh True to reload the upload grid
 */
function dismissModal(p_reload_eh)
{
	if (!p_reload_eh && !props.modalError.error_eh && changesMade.value)
	{
		toggleDiscardChangesModal(true)
	}
	else
	{
		closeModal(p_reload_eh)
	}
}

/**
 * Show or Hide the Discard Changes modal
 * @param {boolean} p_show_eh True to show, false to hide
 */
function toggleDiscardChangesModal(p_show_eh)
{
	showDiscardChangesModal.value = p_show_eh
}

/**
 * Do cleanup, show success toast and tell the UploadDocumentPage to close the modal
 * @param p_reload_eh
 */
function closeModal(p_reload_eh)
{
	isSaving.value = false
	toggleDiscardChangesModal(false)
	if (p_reload_eh) toaster.success("Your document has been successfully added.")
	emit('evCloseModal', 'run', p_reload_eh)
}

function validateDate(p_date, p_id)
{
	let valid_eh = true;
	switch (p_id)
	{
		case 'datepicker_from':
			if (!date_from.value.date)
			{
				validation.value.add(p_id, "Date From is required")
				valid_eh = false;
			}
			else if (p_date.length > 0)
			{
				const from_date = new Date(p_date[0])
				if (date_to.value.date !== null && from_date > new Date(date_to.value.getDate()))
				{
					validation.value.add(p_id, "Date From cannot be after Date To")
					valid_eh = false;
				}
				else
				{
					validation.value.remove(p_id)
				}
			}
			else
			{
				validation.value.add(p_id, "Date From is required.")
				valid_eh = false;
			}

			break
		case 'datepicker_to':
			if (!date_to.value.date)
			{
				validation.value.add(p_id, "Date To is required")
				valid_eh = false;
			}
			else if (p_date.length > 0)
			{
				const to_date = new Date(p_date[0])
				if (date_from.value.date !== null && to_date < new Date(date_from.value.getDate()))
				{
					validation.value.add(p_id, "Date To cannot be before Date From")
					valid_eh = false;
				}
				else
				{
					validation.value.remove(p_id)
				}
			}
			else
			{
				validation.value.add(p_id, "Date To is required.")
				valid_eh = false;
			}
			break

		default:

	}

	return valid_eh;
}

const recipientLabel = computed(() => {
	if (include_default_recipients.value)
	{
		return 'Additional Recipients';
	}
	else
	{
		return "Recipients";
	}
})

function getInitialFromDate() {
	const now = new Date();
	switch (props.report.frequency)
	{
		case "weekly":
			return Format.dateToStr(startOfWeek(now), '-', 'YYYY-MM-DD')
		case "monthly":
			return Format.dateToStr(startOfMonth(now), '-', 'YYYY-MM-DD')
		case "quarterly":
			return Format.dateToStr(startOfQuarter(now), '-', 'YYYY-MM-DD')
		case "yearly":
			return Format.dateToStr(startOfYear(now), '-', 'YYYY-MM-DD')
		default:
			return Format.dateToStr(startOfDay(now), '-', 'YYYY-MM-DD')
	}
}

function getInitialToDate() {
	const now = new Date();
	switch (props.report.frequency)
	{
		case "weekly":
			return Format.dateToStr(endOfWeek(now), '-', 'YYYY-MM-DD')
		case "monthly":
			return Format.dateToStr(endOfMonth(now), '-', 'YYYY-MM-DD')
		case "quarterly":
			return Format.dateToStr(endOfQuarter(now), '-', 'YYYY-MM-DD')
		case "yearly":
			return Format.dateToStr(endOfYear(now), '-', 'YYYY-MM-DD')
		default:
			return Format.dateToStr(endOfDay(now), '-', 'YYYY-MM-DD')
	}
}

</script>

<template>
	<div>
		<BaseModal :show="open" border-classes="border-t-6 border-tertiary">
			<button autofocus class="sr-only">Hidden anchor</button>
			<div class="w-136 h-192">
				<div class="flex justify-between border-b border-neutral w-full pb-6">
					<p class="flex-grow text-xl font-bold text-heading">Run Scheduled Report</p>
					<img src="@/assets/images/icon/dismiss.svg" alt="Dismiss" title="Dismiss" class="cursor-pointer size-4" @click.prevent="dismissModal(false)">
				</div>

				<div v-if="props.modalError.error_eh">
					<div class="w-130 mt-6 text-error font-bold">{{ props.modalError.error_msg }}</div>
				</div>

				<form>
					<div class="flex flex-col mt-5">
						<InputText label="Report Name" :required="false" type="text" placeholder="" input-height="h-12" autocomplete="off"
						           v-model="report_name" :errors="validation.errors" error-field="name" :disabled="true"  />

						<div v-if="props.report.settings.require_date_range">
							<DatePicker id="datepicker_from" v-model="date_from" placeholder="Select a date" label="Date From" @onClose="validateDate" :errors="validation.errors" error-field="datepicker_from" />
							<DatePicker id="datepicker_to" v-model="date_to" placeholder="Select a date" label="Date To" @onClose="validateDate" :errors="validation.errors" error-field="datepicker_to" />
						</div>

						<CheckBox id="include_default_recipients" v-model="include_default_recipients" class="mt-2 mb-2" label="Include Default Recipients?" @onCheckChanged="validateFields" ></CheckBox>
						<UserLookupComboBox id="recipients" v-model="selectedRecipients" class="mt-2" :title="recipientLabel" placeholder="Enter name or email"
						                    label-class="font-medium text-label !mb-2" input-height="h-10" icon="edit" maxSearchResultsHeight="max-h-60"
						                    endpoint="scheduled-reports/user-search"
						                    :endpoint_params="{ 'report_id': report.id, 'organization_id': authStore.activeOrganizationId, 'include_default_recipients': include_default_recipients }"
						                    :enterable="true" :showOptionsOnTop="true" :showOptionsOutsideModal="false" :required="!include_default_recipients" :errors="validation.errors" error-field="recipients" :disabled="false" />
					</div>
					<!-- Save/Cancel -->
					<div class="flex w-full flex-col-reverse sm:flex-row mt-4">
						<div class="sm:mr-auto mt-1 sm:mt-0"></div><!-- remove if showing Deactivate -->
						<SubmitButton label="Cancel" :type="ButtonType.Outline" :size="ButtonSize.Small" @click.prevent="dismissModal(false)"
						              class="mt-3 sm:mt-0" :class="{'sm:mr-4': true}" />
						<SubmitButton label="Run" :size="ButtonSize.Small" @click.prevent="submit" :disabled="isSaving" :loading="isSaving" />
					</div>
				</form>
			</div>
		</BaseModal>

		<Teleport to="html">
			<MultiButtonModal
				:show="showDiscardChangesModal"
				icon="info"
				title="Discard Changes"
				message="Are you sure?"
				subMessage="Leaving now will discard any changes made."
				:buttons="[
									new ModalButtonData(
										'Cancel', () => { toggleDiscardChangesModal(false) }, ButtonType.Outline, ButtonSize.Small
									),
									new ModalButtonData(
										'Yes', () => { closeModal(false) }, ButtonType.Default, ButtonSize.Small
									)
								]"
			/>
		</Teleport>
	</div>
</template>
